import { isEmpty } from 'lodash';
import { noCardLinkDirective } from './noCardLinkDirective';

/**
 * Directives carry what the client knows and the agent otherwise cannot. The gateway holds
 * the system prompt, so anything it does not already say has to ride along in the prompt itself.
 *
 * Almost nothing is left. The agent's base instructions, its response conventions, its
 * groundedness rules and its card-linking rules are the backend's, and so now are the two this
 * client used to append to every turn: the answer's language is a body field (`language`), and the
 * dictation mark is gone. **The Ask panel adds nothing at all** — what the gateway receives as the
 * prompt is exactly what the user typed, which is also what history stores and shows back.
 *
 * What remains is the one instruction no server-side prompt can reach: the summary widgets want no
 * links, and the gateway's system prompt asks for one on every card the agent names.
 */

/**
 * Directives for a Capture: none.
 *
 * **A Capture with no words returns nothing at all**, so `buildAgUiRequestBody` leaves `prompt` off
 * the body entirely. That is a photo on its own, whether it is the first Capture or a fifth: there
 * is no question, and a turn whose whole content was blank space would still read as the user's,
 * because that is the field it arrives in.
 *
 * Deliberately **no instruction to create a card**. The request's `action` is what tells the
 * gateway a Capture is a card being built, and with it set the gateway owns the instruction
 * wording outright. Nothing about links either: nothing renders this answer — it is parsed into
 * field data and the form takes over. Nothing about the form's current values, which are a body
 * field now (`baseline`) rather than a paragraph of prompt.
 */
export function withCaptureDirectives(prompt: string): string {
  return isEmpty(prompt.trim()) ? '' : prompt;
}

/**
 * Directives for the summary widgets: one line ruling out links.
 *
 * A summary is about the one record the user already has open, so the only link it could
 * offer points back at that record, and `AiSummary` renders its markdown without the
 * transcript's link handler — a link it did produce would leave the app on a full page load
 * rather than routing. Saying nothing is not the same as saying no, and here it is not even
 * neutral: the gateway's system prompt asks for a markdown link on every card the agent names.
 * `noCardLinkDirective` overrides it explicitly.
 */
export function withSummaryDirectives(prompt: string): string {
  return [prompt, noCardLinkDirective()].join('\n\n');
}
